<template>
  <div class="analog-line">
    <v-alert
      v-if="hasChanges"
      type="warning"
      dense
      class="mb-4"
    >
      Есть несохранённые изменения. Нажмите «Сохранить», чтобы отправить настройки на устройство.
    </v-alert>

    <v-progress-linear
      v-if="loadingAnalogLine"
      indeterminate
      color="primary"
      height="3"
    />
    <div class="analog-line__header">
      <v-icon color="primary" class="analog-line__header-icon">mdi-phone-outline</v-icon>
      <h2 class="analog-line__title">Аналоговая линия</h2>
    </div>
    <v-form ref="form" lazy-validation>
      <v-row class="analog-line-info">
        <v-col sm="4">
          <v-row>
            <v-col cols="12" sm="9">
              <h5 class="mb-2">Режим работы коммутатора</h5>
              <v-autocomplete
                v-model="workingMode"
                :items="workingModes"
                item-text="text"
                item-value="value"
                label="Выберите режим"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-tune-variant"
                class="analog-line__field"
                @change="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="9">
              <h5 class="mb-2">Коммутатор координатно-матричный</h5>
              <v-btn
                color="primary"
                outlined
                rounded
                class="text-none"
                :disabled="!isAnalogLineAnalysisAvailable"
                @click="kkmModalOpen = true"
              >
                <v-icon left small>mdi-table-edit</v-icon>
                {{ workingMode === 2 ? 'Таблица адресации (цифровая)' : 'Настройки ККМ (тайминги и таблицы)' }}
              </v-btn>
            </v-col>
            <v-col cols="12" sm="9" class="py-0">
              <h5 class="mb-2">Аудио</h5>
              <div>
                <h2>Громкость динамика</h2>
                <v-slider
                  v-model="audioVolume"
                  max="15"
                  color="blue"
                  track-color="gray"
                  thumb-label
                  @change="markDirty"
                />
              </div>
              <v-autocomplete
                v-model="soundProcessingMode"
                :items="vQEMode"
                label="Режим обработки звука"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-waveform"
                class="analog-line__field"
                @change="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="9" class="py-0">
              <v-autocomplete
                v-model="echoCancellationMode"
                :items="ehoMode"
                label="Режим компенсации эхо"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-microphone"
                class="analog-line__field"
                @change="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="9" class="py-0">
              <v-text-field
                v-model="manualEchoCancellation"
                label="Ручная настройка компенсации эхо"
                :rules="rules.universal"
                :disabled="echoCancellationMode !== 1"
                outlined
                dense
                flat
                type="text"
                required
                prepend-inner-icon="mdi-tune"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="9" class="py-0">
              <v-checkbox
                label="Автоматическая регулировка чувствительности микрофона"
                hide-details
                v-model="autoMicGain"
                dense
                color="success"
                class="pt-0 pb-3"
                flat
                @change="markDirty"
              />
              <v-checkbox
                label="Активное шумоподавление"
                hide-details
                v-model="ANR"
                dense
                color="success"
                class="pt-0 pb-4"
                flat
                @change="markDirty"
              />
              <div>
                <h2>Чувствительность микрофона</h2>
                <v-slider
                  v-model="audioMic"
                  :disabled="autoMicGain"
                  max="15"
                  color="blue"
                  track-color="gray"
                  thumb-label
                  @change="markDirty"
                />
              </div>
            </v-col>
          </v-row>
        </v-col>
        <div>
          <v-col v-if="workingMode === 1" cols="12">
            <v-col cols="12" sm="10" class="py-0">
              <h5 class="mb-2">Аналоговый режим (напряжения)</h5>
              <v-text-field
                v-model="analogLineVoltageIdle"
                label="В состоянии покоя, минимальное, В"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-flash-outline"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="10" class="py-0">
              <v-text-field
                v-model="analogLineVoltageLifted"
                label="Трубка поднята, минимальное, В"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-phone-hangup"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="10" class="py-0">
              <v-text-field
                v-model="analogLineVoltageButtonPressed"
                label="кнопка нажата, минимальное, В"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-gesture-tap"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
          </v-col>
          <v-col v-if="workingMode === 2" cols="12">
            <v-col cols="12" sm="10" class="py-0">
              <h5 class="mb-2">Цифровой режим (тайминги)</h5>
              <v-text-field
                v-model.number="digiLineTimingsStartMs"
                label="Начало передачи сигнала, мс"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-timer-outline"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="10" class="py-0">
              <v-text-field
                v-model.number="digiLineTimingsStopMs"
                label="Остановка передачи сигнала, мс"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-timer-off-outline"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="10" class="py-0">
              <v-text-field
                v-model.number="digiLineTimingsCountPulse0Us"
                label="Длительность импульса «0», мкс"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-waveform"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="10" class="py-0">
              <v-text-field
                v-model.number="digiLineTimingsCountPulse1Us"
                label="Длительность импульса «1», мкс"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-waveform"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="10" class="py-0">
              <v-text-field
                v-model.number="digiLineTimingsButtonPressPulseMs"
                label="Импульс нажатия кнопки, мс"
                :rules="rules.universal"
                type="number"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-gesture-tap"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
            <v-col cols="12" sm="10" class="py-0">
              <h5 class="mb-2">Цифровой режим (напряжения)</h5>
              <v-text-field
                v-model.number="digiLineVoltageLifted"
                label="Трубка поднята, максимальное, В"
                type="number"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-flash-outline"
                class="analog-line__field"
                @input="markDirty"
              />
            </v-col>
          </v-col>
          <v-col sm="10" style="width: 450px">
            <v-col class="pb-0">
              <h5 class="mb-2">Рингтоны</h5>
              <div>
                <h2>Громкость рингтона</h2>
                <v-slider
                  v-model="ringVolume"
                  max="6"
                  color="blue"
                  track-color="gray"
                  thumb-label
                  @change="markDirty"
                />
              </div>
              <v-autocomplete
                v-model="ringType"
                :items="ringTypes"
                label="Режим рингтона"
                outlined
                dense
                flat
                required
                prepend-inner-icon="mdi-bell-ring"
                class="analog-line__field"
                @change="markDirty"
              />
            </v-col>
          </v-col>
        </div>
        <div>
          <v-col sm="10">
            <v-card class="analog-line__card" style="width: 400px">
              <v-col class="pb-0">
                <h5 class="pb-5">Звуковые сообщения</h5>
                <div>
                  <h2>Громкость динамика</h2>
                  <v-slider
                    v-model="speakerVolume"
                    max="15"
                    color="#ff4081"
                    track-color="gray"
                    thumb-label
                  />
                </div>
              </v-col>
              <v-card-actions class="text-right" style="display: block;">
                <v-btn
                  color="primary"
                  outlined
                  rounded
                  class="text-none mt-0"
                  :loading="isLoadingVolume"
                  @click.prevent="updateSpeakerVolume"
                >
                  <v-icon left small>mdi-content-save-outline</v-icon>
                  Сохранить
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col sm="10" class="mt-4">
            <v-card class="analog-line__card" style="width: 450px">
              <v-col class="pb-0">
                <h5 class="pb-2">Измерение аналоговой линии</h5>
                <p class="text-caption text--secondary mb-1">
                  Можно измерить одну квартиру (только «с») или диапазон (заполните «с» и «по»).
                </p>
                <p
                  v-if="apartmentDiapasonHint"
                  class="text-caption text--secondary mb-1"
                >
                  {{ apartmentDiapasonHint }}
                </p>
                <p class="text-caption text--secondary mb-3">
                  За один запрос можно измерить не более {{ maxVoltageMeasureApartments }} квартир.
                </p>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      v-model="numberAbonentFrom"
                      label="Квартира с"
                      :rules="[() => !!numberAbonentFrom || 'Обязательное поле']"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      outlined
                      dense
                      flat
                      required
                      prepend-inner-icon="mdi-account-outline"
                      class="analog-line__field analog-line__no-spinner"
                      @keyup="validateKeyUp"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="numberAbonentTo"
                      label="Квартира по (необязательно)"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      outlined
                      dense
                      flat
                      prepend-inner-icon="mdi-account-multiple-outline"
                      class="analog-line__field analog-line__no-spinner"
                      @keyup="validateKeyUp"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <div class="pl-4 pr-4">
                <template v-if="voltageMeasurements.length">
                  <div
                    v-for="(item, idx) in voltageMeasurements"
                    :key="idx"
                    class="mb-2"
                  >
                    <strong>Кв. {{ item.apartment }}</strong>
                    <span v-if="item.line != null">, линия {{ item.line }}</span>
                    <span v-if="item.voltage != null">, {{ item.voltage }} В</span>
                    <span v-if="item.status"> — {{ item.status }}</span>
                  </div>
                </template>
                <p v-else-if="voltageMessage">{{ voltageMessage }}</p>
              </div>
              <v-card-actions class="text-right analog-line__measure-actions">
                <v-btn
                  color="primary"
                  outlined
                  rounded
                  class="text-none"
                  :loading="loading"
                  :disabled="!canMeasureVoltage"
                  @click.prevent="measurementVoltage"
                >
                  <v-icon left small>mdi-speedometer</v-icon>
                  Измерить
                </v-btn>
                <v-btn
                  v-if="voltageMeasurements.length || voltageMessage"
                  icon
                  color="primary"
                  class="ml-1"
                  :disabled="loading || !canMeasureVoltage"
                  title="Повторить измерение"
                  @click.prevent="measurementVoltage"
                >
                  <v-icon>mdi-update</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </div>
      </v-row>
      <div class="analog-line__actions">
        <v-btn
          color="primary"
          class="text-none"
          rounded
          @click="save"
        >
          <v-icon left>mdi-content-save-outline</v-icon>
          Сохранить
        </v-btn>
        <v-btn
          color="primary"
          outlined
          rounded
          class="text-none ml-2"
          @click.prevent="returnDoorphone()"
        >
          <v-icon left>mdi-close</v-icon>
          Отменить
        </v-btn>
      </div>
    </v-form>

    <KkmAddressingModal
      v-model="kkmModalOpen"
      :working-mode="workingMode"
      :kkmtype="kkmtype"
      :first-location-id="firstLocationId"
      :hundreds="hundreds"
      :digit-handset="digitHandset"
      :timings="kkmTimings"
      @apply="onKkmApply"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { rules } from '../consts';
import KkmAddressingModal from './KkmAddressingModal.vue';
import {
  HUNDRED_KEYS,
  KKM_TYPE_MANUAL,
  createEmptyHundreds,
  createEmptyDigitHandset,
  getVisibleHundredKeys,
  hundredsForKkmtype,
  normalizeDigitHandset,
  fillDigitHandset,
  isDigitHandsetEmpty,
} from './kkmConsts';

const MAX_VOLTAGE_MEASURE_APARTMENTS = 30;

export default {
  name: 'AnalogLine',
  components: { KkmAddressingModal },
  data: () => ({
    response: [],
    rules,
    workingMode: null,
    autoMicGain: false,
    ANR: false,
    audioVolume: 0,
    ringVolume: 0,
    audioMic: 0,
    voltageMessage: '',
    voltageMeasurements: [],
    lastLocationId: null,
    firstLocationId: 1,
    kkmtype: 0,
    kkmTimings: {
      kkmTimingsStartMs: 0,
      kkmTimingsStopMs: 0,
      kkmTimingsCountPulse0Us: 0,
      kkmTimingsCountPulse1Us: 0,
    },
    hundreds: createEmptyHundreds(),
    digitHandset: createEmptyDigitHandset(),
    kkmModalOpen: false,
    isAnalogLineAnalysisAvailable: false,
    hasChanges: false,
    savedSnapshot: null,
    loading: false,
    isLoadingVolume: false,
    loadingAnalogLine: false,
    speakerVolume: 0,
    numberAbonentFrom: null,
    numberAbonentTo: null,
    updateInterval: null,
    analogLineVoltageButtonPressed: null,
    analogLineVoltageLifted: null,
    analogLineVoltageIdle: null,
    digiLineTimingsStartMs: null,
    digiLineTimingsStopMs: null,
    digiLineTimingsCountPulse0Us: null,
    digiLineTimingsCountPulse1Us: null,
    digiLineTimingsButtonPressPulseMs: null,
    digiLineVoltageLifted: null,
    echoCancellationMode: null,
    ringType: null,
    soundProcessingMode: null,
    manualEchoCancellation: '',
    workingModes: [
      { text: 'Аналоговый', value: 1 },
      { text: 'Цифровой', value: 2 },
    ],
    vQEMode: [
      { text: 'Обычный', value: 0 },
      { text: 'Музыкальный', value: 1 },
      { text: 'Зашумленная', value: 2 },
    ],
    ringTypes: [
      { text: 'По умолчанию', value: 0 },
      { text: 'К Элизе', value: 1 },
      { text: 'Тишина', value: 2 },
      { text: 'На прекрасном голубом Дунае', value: 3 },
    ],
    ehoMode: [
      { text: 'Выключено', value: -1 },
      { text: 'Автоматический режим', value: 0 },
      { text: 'Ручная настройка', value: 1 },
      { text: 'усиленное эхоподавление', value: 2 },
    ],
    valid: true,
  }),
  async created () {
    await this.getSettings();
  },
  beforeDestroy () {
    clearInterval(this.updateInterval);
  },
  computed: {
    ...mapGetters({
      doorphone: 'Doorphone/doorphoneEditDoorphone',
    }),
    maxVoltageMeasureApartments () {
      return MAX_VOLTAGE_MEASURE_APARTMENTS;
    },
    apartmentDiapasonHint () {
      const d = this.doorphone;
      if (d?.diapason_from != null && d?.diapason_to != null) {
        return `Диапазон квартир домофона: ${d.diapason_from}–${d.diapason_to}.`;
      }
      if (d?.diapason) {
        return `Диапазон квартир домофона: ${d.diapason}.`;
      }
      if (this.firstLocationId != null && this.lastLocationId != null) {
        return `Нумерация на устройстве (ККМ): ${this.firstLocationId}–${this.lastLocationId}.`;
      }
      return null;
    },
    canMeasureVoltage () {
      return !!this.numberAbonentFrom && this.isMeasurementRangeValid;
    },
    isMeasurementRangeValid () {
      const from = Number(this.numberAbonentFrom);
      if (!this.numberAbonentFrom || Number.isNaN(from)) {
        return false;
      }
      const toRaw = this.numberAbonentTo;
      if (toRaw === '' || toRaw == null) {
        return true;
      }
      const to = Number(toRaw);
      if (Number.isNaN(to) || to < from) {
        return false;
      }
      return to - from + 1 <= MAX_VOLTAGE_MEASURE_APARTMENTS;
    },
  },
  methods: {
    ...mapActions({
      load: 'AnalyticsServer/showServer',
      getAnalogLineAnalysis: 'Doorphone/getAnalogLineAnalysis',
      updateAnalogLineAnalysis: 'Doorphone/updateAnalogLineAnalysis',
      getMeasurementAnalogLine: 'Doorphone/getMeasurementAnalogLine',
      getAudioMessage: 'Doorphone/getAudioMessage',
      updateAudioMessage: 'Doorphone/updateAudioMessage',
    }),
    normalizeHundreds (data) {
      const hundreds = createEmptyHundreds();
      HUNDRED_KEYS.forEach((key) => {
        if (Array.isArray(data[key]) && data[key].length === 100) {
          hundreds[key] = [...data[key]];
        }
      });
      return hundreds;
    },
    buildFormState () {
      return {
        analogLineVoltageIdle: this.analogLineVoltageIdle,
        analogLineVoltageLifted: this.analogLineVoltageLifted,
        analogLineVoltageButtonPressed: this.analogLineVoltageButtonPressed,
        digiLineTimingsStartMs: this.digiLineTimingsStartMs,
        digiLineTimingsStopMs: this.digiLineTimingsStopMs,
        digiLineTimingsCountPulse0Us: this.digiLineTimingsCountPulse0Us,
        digiLineTimingsCountPulse1Us: this.digiLineTimingsCountPulse1Us,
        digiLineTimingsButtonPressPulseMs: this.digiLineTimingsButtonPressPulseMs,
        digiLineVoltageLifted: this.digiLineVoltageLifted,
        workingMode: this.workingMode,
        audioVolume: this.audioVolume,
        audioMic: this.audioMic,
        soundProcessingMode: this.soundProcessingMode,
        echoCancellationMode: this.echoCancellationMode,
        autoMicGain: this.autoMicGain,
        ringVolume: this.ringVolume,
        ringType: this.ringType,
        ANR: this.ANR,
        manualEchoCancellation: this.manualEchoCancellation,
        firstLocationId: this.firstLocationId,
        lastLocationId: this.lastLocationId,
        kkmtype: this.kkmtype,
        kkmTimings: { ...this.kkmTimings },
        hundreds: JSON.parse(JSON.stringify(this.hundreds)),
        digitHandset: [...this.digitHandset],
      };
    },
    applyFormState (state) {
      this.analogLineVoltageIdle = state.analogLineVoltageIdle;
      this.analogLineVoltageLifted = state.analogLineVoltageLifted;
      this.analogLineVoltageButtonPressed = state.analogLineVoltageButtonPressed;
      this.digiLineTimingsStartMs = state.digiLineTimingsStartMs;
      this.digiLineTimingsStopMs = state.digiLineTimingsStopMs;
      this.digiLineTimingsCountPulse0Us = state.digiLineTimingsCountPulse0Us;
      this.digiLineTimingsCountPulse1Us = state.digiLineTimingsCountPulse1Us;
      this.digiLineTimingsButtonPressPulseMs = state.digiLineTimingsButtonPressPulseMs;
      this.digiLineVoltageLifted = state.digiLineVoltageLifted;
      this.workingMode = state.workingMode;
      this.audioMic = state.audioMic;
      this.audioVolume = state.audioVolume;
      this.ringVolume = state.ringVolume;
      this.ringType = state.ringType;
      this.echoCancellationMode = state.echoCancellationMode;
      this.soundProcessingMode = state.soundProcessingMode;
      this.manualEchoCancellation = state.manualEchoCancellation;
      this.autoMicGain = state.autoMicGain;
      this.ANR = state.ANR;
      this.lastLocationId = state.lastLocationId;
      this.firstLocationId = state.firstLocationId;
      this.kkmtype = state.kkmtype;
      this.kkmTimings = { ...state.kkmTimings };
      this.hundreds = JSON.parse(JSON.stringify(state.hundreds));
      this.digitHandset = normalizeDigitHandset(state.digitHandset);
    },
    captureSnapshot () {
      this.savedSnapshot = JSON.stringify(this.buildFormState());
      this.hasChanges = false;
    },
    markDirty () {
      if (!this.savedSnapshot) {
        return;
      }
      this.hasChanges = JSON.stringify(this.buildFormState()) !== this.savedSnapshot;
    },
    onKkmApply ({ kkmtype, firstLocationId, hundreds, timings, digitHandset }) {
      if (firstLocationId != null) {
        this.firstLocationId = firstLocationId;
      }
      if (Array.isArray(digitHandset)) {
        this.digitHandset = normalizeDigitHandset(digitHandset);
      }
      if (kkmtype != null) {
        this.kkmtype = kkmtype;
        this.hundreds = hundredsForKkmtype(
          { ...this.hundreds, ...hundreds },
          kkmtype,
        );
      }
      if (timings) {
        this.kkmTimings = timings;
      }
      this.markDirty();
    },
    buildSavePayload () {
      const data = {
        analog_line_mode: this.workingMode,
        audio_volume: this.audioVolume,
        audio_mic: this.audioMic,
        VQEMode: this.soundProcessingMode,
        EchoMode: this.echoCancellationMode,
        AutoMicGain: this.autoMicGain,
        ringVolume: this.ringVolume,
        ringType: this.ringType,
        anr: this.ANR ? 1 : 0,
        EchoParamLine: this.manualEchoCancellation,
        firstLocationId: this.firstLocationId,
      };

      if (this.workingMode === 1) {
        data.analogLineVoltageIdle = this.analogLineVoltageIdle;
        data.analogLineVoltageLifted = this.analogLineVoltageLifted;
        data.analogLineVoltageButtonPressed = this.analogLineVoltageButtonPressed;
        data.kkmtype = this.kkmtype;

        if (this.kkmtype === KKM_TYPE_MANUAL) {
          data.kkmTimingsStartMs = this.kkmTimings.kkmTimingsStartMs;
          data.kkmTimingsStopMs = this.kkmTimings.kkmTimingsStopMs;
          data.kkmTimingsCountPulse0Us = this.kkmTimings.kkmTimingsCountPulse0Us;
          data.kkmTimingsCountPulse1Us = this.kkmTimings.kkmTimingsCountPulse1Us;
        }

        getVisibleHundredKeys(this.kkmtype).forEach((key) => {
          data[key] = this.hundreds[key];
        });
      }

      if (this.workingMode === 2) {
        data.digiLineTimingsStartMs = Number(this.digiLineTimingsStartMs);
        data.digiLineTimingsStopMs = Number(this.digiLineTimingsStopMs);
        data.digiLineTimingsCountPulse0Us = Number(this.digiLineTimingsCountPulse0Us);
        data.digiLineTimingsCountPulse1Us = Number(this.digiLineTimingsCountPulse1Us);
        data.digiLineTimingsButtonPressPulseMs = Number(this.digiLineTimingsButtonPressPulseMs);
        if (this.digiLineVoltageLifted != null && this.digiLineVoltageLifted !== '') {
          data.digiLineVoltageLifted = Number(this.digiLineVoltageLifted);
        }
        data.digitHandset = [...this.digitHandset];
      }

      if (this.lastLocationId != null) {
        data.lastLocationId = this.lastLocationId;
      }

      return data;
    },
    validateKeyUp (e) {
      if (e.keyCode === 13) {
        this.measurementVoltage();
      }
    },
    returnDoorphone () {
      this.$router.push({ name: 'DetailDoorphone', params: { id: this.$route.params.id } });
    },
    unwrapApiData (payload) {
      if (payload?.data != null && payload.analogLineVoltageIdle == null && payload.analog_line_mode == null) {
        return payload.data;
      }
      return payload;
    },
    applyAnalysisData (raw) {
      const data = this.unwrapApiData(raw);
      this.response = data;
      this.analogLineVoltageIdle = data.analogLineVoltageIdle;
      this.analogLineVoltageButtonPressed = data.analogLineVoltageButtonPressed;
      this.analogLineVoltageLifted = data.analogLineVoltageLifted;
      this.digiLineTimingsStartMs = data.digiLineTimingsStartMs ?? null;
      this.digiLineTimingsStopMs = data.digiLineTimingsStopMs ?? null;
      this.digiLineTimingsCountPulse0Us = data.digiLineTimingsCountPulse0Us ?? null;
      this.digiLineTimingsCountPulse1Us = data.digiLineTimingsCountPulse1Us ?? null;
      this.digiLineTimingsButtonPressPulseMs = data.digiLineTimingsButtonPressPulseMs ?? null;
      this.digiLineVoltageLifted = data.digiLineVoltageLifted ?? null;
      this.audioMic = data.audio_mic;
      this.audioVolume = data.audio_volume;
      this.ringVolume = data.ringVolume;
      this.ringType = data.ringType;
      const mode = data.analog_line_mode;
      this.workingMode = mode === 1 || mode === 2 ? mode : null;
      this.echoCancellationMode = data.EchoMode;
      this.soundProcessingMode = data.VQEMode;
      this.manualEchoCancellation = data.EchoParamLine;
      this.autoMicGain = data.AutoMicGain;
      this.ANR = data.anr;
      this.lastLocationId = data.lastLocationId;
      this.firstLocationId = data.firstLocationId ?? 1;
      this.kkmtype = data.kkmtype ?? 0;
      this.kkmTimings = {
        kkmTimingsStartMs: data.kkmTimingsStartMs ?? 0,
        kkmTimingsStopMs: data.kkmTimingsStopMs ?? 0,
        kkmTimingsCountPulse0Us: data.kkmTimingsCountPulse0Us ?? 0,
        kkmTimingsCountPulse1Us: data.kkmTimingsCountPulse1Us ?? 0,
      };
      this.hundreds = hundredsForKkmtype(
        this.normalizeHundreds(data),
        this.kkmtype,
      );
      this.digitHandset = normalizeDigitHandset(data.digitHandset);
      if (isDigitHandsetEmpty(this.digitHandset)) {
        this.digitHandset = fillDigitHandset(this.firstLocationId);
      }
      this.isAnalogLineAnalysisAvailable = true;
      this.captureSnapshot();
    },
    getApiErrorMessage (err, fallback) {
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const first = Object.values(fieldErrors).flat().find(Boolean);
        if (first) {
          return first;
        }
      }
      return err?.response?.data?.error || err?.response?.data?.message || fallback;
    },
    getSettings () {
      this.loadingAnalogLine = true;
      this.isAnalogLineAnalysisAvailable = false;
      this.kkmModalOpen = false;
      this.getAnalogLineAnalysis(this.$route.params.id)
        .then((data) => {
          if (data?.status === false) {
            this.$flash_warning(data.error || 'Не удалось получить настройки');
            return;
          }
          this.applyAnalysisData(data);
        })
        .catch((err) => {
          console.error(err);
          this.$flash_warning(this.getApiErrorMessage(err, 'Не удалось получить настройки'));
        })
        .finally(() => {
          this.loadingAnalogLine = false;
        });
      this.getAudioMessage(this.$route.params.id)
        .then((data) => {
          this.speakerVolume = data.volume;
        })
        .catch((err) => {
          console.error(err);
          this.$flash_warning('Не удалось получить настройки громкости динамика');
        });
    },
    async updateSpeakerVolume () {
      try {
        this.isLoadingVolume = true;
        await this.updateAudioMessage({
          id: this.$route.params.id,
          volume: this.speakerVolume,
        });
        this.$flash_success('Успешно!');
      } catch (e) {
        console.error(e);
        this.$flash_warning('Не удалось обновить громкость динамика');
      } finally {
        this.isLoadingVolume = false;
      }
    },
    parseVoltageResponse (payload) {
      const unwrapped = this.unwrapApiData(payload);
      const data = unwrapped?.data != null && unwrapped.measurements == null && unwrapped.apartment == null
        ? unwrapped.data
        : unwrapped;
      if (Array.isArray(data?.measurements)) {
        this.voltageMeasurements = data.measurements;
        this.voltageMessage = '';
      } else if (data && (data.apartment != null || data.voltage != null || data.message)) {
        this.voltageMeasurements = [data];
        this.voltageMessage = data.message || '';
      } else {
        this.voltageMeasurements = [];
        this.voltageMessage = data?.message || '';
      }
    },
    validateMeasurementRange () {
      const from = Number(this.numberAbonentFrom);
      const numberTo = this.numberAbonentTo;

      if (!this.numberAbonentFrom || Number.isNaN(from)) {
        this.$flash_warning('Укажите квартиру «с»');
        return false;
      }

      if (numberTo === '' || numberTo == null) {
        return true;
      }

      const to = Number(numberTo);
      if (Number.isNaN(to)) {
        this.$flash_warning('Некорректное значение «по»');
        return false;
      }
      if (to < from) {
        this.$flash_warning('Значение «по» должно быть не меньше «с»');
        return false;
      }
      const count = to - from + 1;
      if (count > MAX_VOLTAGE_MEASURE_APARTMENTS) {
        this.$flash_warning(
          `За один запрос можно измерить не более ${MAX_VOLTAGE_MEASURE_APARTMENTS} квартир (выбрано ${count})`,
        );
        return false;
      }
      return true;
    },
    async measurementVoltage () {
      if (!this.validateMeasurementRange()) {
        return;
      }

      const numberTo = this.numberAbonentTo;

      try {
        this.loading = true;
        const payload = await this.getMeasurementAnalogLine({
          id: this.$route.params.id,
          number: this.numberAbonentFrom,
          numberTo: numberTo !== '' && numberTo != null ? numberTo : undefined,
        });
        if (payload?.status === false) {
          this.$flash_warning(payload.error || 'Не удалось измерить аналоговую линию');
          return;
        }
        this.parseVoltageResponse(payload);
        this.$flash_success('Успешно!');
      } catch (error) {
        console.error(error);
        this.$flash_warning(
          this.getApiErrorMessage(error, 'Не удалось измерить аналоговую линию'),
        );
      } finally {
        this.loading = false;
      }
    },
    save () {
      const data = this.buildSavePayload();
      this.updateAnalogLineAnalysis({
        id: this.$route.params.id,
        data,
      })
        .then((body) => {
          if (body?.status === false) {
            this.$flash_warning(body.error || 'Ошибка сохранения настроек аналоговой линии');
            return;
          }
          this.$flash_success('Изменения успешно сохранены');
          this.captureSnapshot();
        })
        .catch((err) => {
          this.$flash_warning(
            this.getApiErrorMessage(err, 'Ошибка сохранения настроек аналоговой линии'),
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.analog-line__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.analog-line__header-icon {
  flex-shrink: 0;
}

.analog-line__title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.01em;
}

.analog-line__card {
  border-radius: 12px;
}

.analog-line__field ::v-deep .v-input__prepend-inner .v-icon {
  color: rgba(0, 0, 0, 0.5);
}

.analog-line__no-spinner ::v-deep input {
  -moz-appearance: textfield;
  appearance: textfield;
}

.analog-line__no-spinner ::v-deep input::-webkit-outer-spin-button,
.analog-line__no-spinner ::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.analog-line__measure-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px;
}

.analog-line__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 32px;
}

@media screen and (max-width: 600px) {
  .analog-line-info {
    display: inherit;
  }
}
</style>
